import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Radio, Spin, Alert } from 'antd';
import { ChevronDown, Search, Check } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { multichoixService } from '../../../services/multichoixService';
import { useAutoSelectSingleOption } from '../../../hooks/useAutoSelectSingleOption';
import { useIsMobile } from '@client/hooks/useIsMobile';
import MobileSheetBase from '@client/components/mobile/MobileSheetBase';

/**
 * Split comma-separated IDs into individual IDs
 */
const splitIds = (id) => {
  if (!id) return [];
  if (typeof id === 'string' && id.includes(',')) {
    return id.split(',').map((i) => i.trim()).filter(Boolean);
  }
  return [id];
};

/**
 * Check if an option is selected (ANY of its IDs are in selectedIds)
 */
const isOptionSelected = (optionId, selectedIds) => {
  const ids = splitIds(optionId);
  return ids.some((id) => selectedIds.includes(id));
};

const ApplicationTypeStepContent = ({
  step,
  value,
  selectionLabels,
  allSelections,
  onChange,
  onAutoAdvance,
  mode,
}) => {
  const { t } = useTranslation('treatment-search');
  const isMobile = useIsMobile();
  // SP-430 M103/M106: Multichoix allows a SINGLE application type (radio
  // buttons). Extraction keeps multi-selection with checkboxes (validated
  // Conforme on the Excel sheet).
  const isSingleChoice = mode !== 'extraction';
  const [selectedIds, setSelectedIds] = useState(value || []);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [mobileSearchValue, setMobileSearchValue] = useState('');

  // Sync selectedIds with value prop
  useEffect(() => {
    setSelectedIds(value || []);
  }, [value]);

  // Transform selections for API - includes ALL previous selections
  const apiSelections = multichoixService.transformSelectionsForApi(allSelections);
  const hasCultures = apiSelections.cultures.length > 0;

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'multichoix-step-6',
      apiSelections.cultures,
      apiSelections.cultural_practices,
      apiSelections.treatment_type,
      apiSelections.enemies,
      apiSelections.intervention_period,
    ],
    queryFn: () => multichoixService.getStepOptions(6, apiSelections),
    enabled: hasCultures,
    staleTime: 5 * 60 * 1000,
  });

  const types = data?.options || [];

  // Mobile: Get display value
  const mobileDisplayValue = useMemo(() => {
    const selected = types.filter((tp) => isOptionSelected(tp.id, selectedIds));
    if (selected.length === 0) return '';
    if (isSingleChoice) return selected[0].label;
    if (selected.length === types.length) return t('multichoix.steps.application_type.all_selected', 'Tous les types');
    return `${selected.length} ${t('multichoix.common.selected', 'sélectionné(s)')}`;
  }, [selectedIds, types, isSingleChoice, t]);

  // Mobile: Filter options
  const filteredTypes = useMemo(() => {
    if (!mobileSearchValue) return types;
    const search = mobileSearchValue.toLowerCase();
    return types.filter((type) =>
      type.label.toLowerCase().includes(search)
    );
  }, [types, mobileSearchValue]);

  const handleItemChange = useCallback((itemId, itemLabel, checked) => {
    const itemIds = splitIds(itemId);
    let newIds;
    let newLabels;

    if (checked) {
      newIds = [...new Set([...selectedIds, ...itemIds])];
      newLabels = [
        ...(selectionLabels || []),
        { value: itemIds[0], label: itemLabel },
      ];
    } else {
      newIds = selectedIds.filter((id) => !itemIds.includes(id));
      newLabels = (selectionLabels || []).filter((l) => !itemIds.includes(l.value));
    }

    setSelectedIds(newIds);
    onChange(newIds, newLabels);
  }, [selectedIds, selectionLabels, onChange]);

  // SP-430: if only one application type is possible, select it by default.
  // (Last step — onAutoAdvance is undefined, so it only selects; the auto-launch
  // on the final criterion is handled separately, see M11/Group E.)
  const selectSingleOption = useCallback((opt) => {
    const itemIds = splitIds(opt.id);
    setSelectedIds([itemIds[0]]);
    onChange([itemIds[0]], [{ value: itemIds[0], label: opt.label }]);
  }, [onChange]);

  useAutoSelectSingleOption({
    options: types,
    isLoading,
    value,
    onSelect: selectSingleOption,
    onAdvance: onAutoAdvance,
  });

  // SP-430 M106: single-choice (multichoix) selection commits one application type.
  const selectedOptionId = useMemo(() => {
    const match = types.find((type) => isOptionSelected(type.id, selectedIds));
    return match ? match.id : undefined;
  }, [types, selectedIds]);

  const handleMobileSingleSelect = useCallback((opt) => {
    setSheetOpen(false);
    selectSingleOption(opt);
  }, [selectSingleOption]);

  if (!hasCultures) {
    return (
      <Alert
        type="info"
        message={t('multichoix.steps.application_type.select_previous', 'Complétez les étapes précédentes')}
        showIcon
      />
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message={t('common.error', 'Une erreur est survenue')}
        description={error.message}
        showIcon
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spin tip={t('common.loading', 'Chargement...')} />
      </div>
    );
  }

  if (types.length === 0) {
    return (
      <Alert
        type="warning"
        message={t('multichoix.steps.application_type.no_options', 'Aucun type d\'application disponible')}
        showIcon
        className="text-xs sm:text-sm"
      />
    );
  }

  // Mobile render
  if (isMobile) {
    return (
      <div className="w-full">
        {/* Mobile trigger */}
        <div
          onClick={() => setSheetOpen(true)}
          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between"
        >
          <span className={`text-xs flex-1 truncate ${mobileDisplayValue ? 'text-gray-900' : 'text-gray-400'}`}>
            {mobileDisplayValue || t(step.placeholder)}
          </span>
          {isLoading ? (
            <Spin size="small" className="ml-2" />
          ) : (
            <ChevronDown size={16} className="ml-2 text-gray-400 flex-shrink-0" />
          )}
        </div>

        {/* Mobile sheet */}
        <MobileSheetBase
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={t(step.title)}
          showDone={!isSingleChoice}
          onDone={() => setSheetOpen(false)}
        >
          {/* Search */}
          <div className="sticky top-0 px-3 py-2 bg-white border-b border-gray-100 z-10 flex-shrink-0">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                className="w-full h-10 pl-9 pr-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#207d54]"
                placeholder={t('common|Rechercher...')}
                value={mobileSearchValue}
                onChange={(e) => setMobileSearchValue(e.target.value)}
              />
            </div>
          </div>

          {/* Options list */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Spin size="default" />
              </div>
            )}

            {!isLoading && filteredTypes.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-center">
                <div className="text-xs">{t('common|Aucun résultat trouvé')}</div>
              </div>
            )}

            {!isLoading && filteredTypes.length > 0 && (
              <div className="py-1">
                {filteredTypes.map((type) => {
                  const isSelected = isOptionSelected(type.id, selectedIds);
                  return (
                    <div
                      key={type.id}
                      className={`flex items-center min-h-[44px] px-3 py-2 cursor-pointer ${isSelected ? 'bg-green-50' : ''}`}
                      onClick={() =>
                        isSingleChoice
                          ? handleMobileSingleSelect(type)
                          : handleItemChange(type.id, type.label, !isSelected)
                      }
                    >
                      <div
                        className={`w-5 h-5 border-2 mr-3 flex-shrink-0 flex items-center justify-center ${
                          isSingleChoice ? 'rounded-full' : 'rounded'
                        } ${
                          isSelected ? 'bg-[#207d54] border-[#207d54]' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && (
                          isSingleChoice
                            ? <div className="w-2 h-2 rounded-full bg-white" />
                            : <Check size={14} className="text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-900">{type.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </MobileSheetBase>
      </div>
    );
  }

  // Desktop render — single choice (multichoix): radio buttons stacked.
  if (isSingleChoice) {
    return (
      <div className="green-radio max-h-[250px] sm:max-h-[300px] overflow-y-auto">
        <Radio.Group
          value={selectedOptionId}
          onChange={(e) => {
            const opt = types.find((type) => type.id === e.target.value);
            if (opt) selectSingleOption(opt);
          }}
          className="flex flex-col gap-1"
        >
          {types.map((type) => (
            <Radio key={type.id} value={type.id} className="py-1 text-xs sm:text-sm">
              <span className="text-xs sm:text-sm">{type.label}</span>
            </Radio>
          ))}
        </Radio.Group>
      </div>
    );
  }

  // Desktop render — multi selection (extraction): checkboxes.
  return (
    <div className="space-y-1 sm:space-y-2 max-h-[250px] sm:max-h-[300px] overflow-y-auto green-checkbox">
      {types.map((type) => (
        <div key={type.id} className="py-1">
          <Checkbox
            checked={isOptionSelected(type.id, selectedIds)}
            onChange={(e) =>
              handleItemChange(type.id, type.label, e.target.checked)
            }
            className="text-xs sm:text-sm"
          >
            <span className="text-xs sm:text-sm">{type.label}</span>
          </Checkbox>
        </div>
      ))}
    </div>
  );
};

export default ApplicationTypeStepContent;
